import React from "react";
import { useNavigate } from "react-router-dom";
import ATMPageHeader from "src/components/UI/atoms/ATMPageHeader/ATMPageHeader";
import ATMPagination from "src/components/UI/atoms/ATMPagination/ATMPagination";
import ATMTable, {
  columnTypes,
} from "src/components/UI/atoms/ATMTable/ATMTable";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import OrderFilterDialogWrapper from "./FilterDialog/OrderFilterDialogWrapper";
import { useLazyGetAllOrdersForExportQuery } from "src/services/OrderService";
import { showToast } from "src/utils/showToaster";

type Props = {
  columns: columnTypes[];
  rows: any[];
  paginationProps: {
    isTableLoading: boolean;
    totalItems: number;
    page: number;
    rowsPerPage: number;
    searchValue: string;
    setPage: (newPage: number) => void;
    setRowsPerPage: (newLimit: number) => void;
    setSearchValue: (newValue: string) => void;
  };
};

// CSV export: escape and quote field if needed
const csvEscape = (val: unknown): string => {
  if (val == null) return "";
  const str = typeof val === "object" ? JSON.stringify(val) : String(val);
  if (/[,"\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

// Convert array of objects to CSV string
const jsonToCsv = (data: Record<string, unknown>[]): string => {
  if (!data?.length) return "";
  const headers = Object.keys(data[0]);
  const headerRow = headers.map(csvEscape).join(",");
  const rows = data.map((row) =>
    headers.map((h) => csvEscape(row[h])).join(",")
  );
  return [headerRow, ...rows].join("\r\n");
};

// Trigger CSV file download
const downloadCsv = (csvContent: string, filename: string) => {
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const OrderListing = ({
  columns,
  rows,
  paginationProps: {
    isTableLoading,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    setPage,
    setRowsPerPage,
    setSearchValue,
  },
}: Props) => {
  const [openFilterDialog, setOpenFilterDialog] =
    React.useState<boolean>(false);
  const navigate = useNavigate();
  const [getAllOrdersForExport, { isLoading: isExportLoading }] =
    useLazyGetAllOrdersForExportQuery();

  const handleDownloadExcel = async () => {
    try {
      const res = await getAllOrdersForExport(undefined);
      if (res.error) {
        showToast("error", (res.error as any)?.data?.message || "Export failed");
        return;
      }
      const raw = res.data as { data?: Record<string, unknown>[] } | undefined;
      const list = Array.isArray(raw) ? raw : raw?.data ?? [];
      if (!list.length) {
        showToast("success", "No orders to export");
        return;
      }
      const csv = jsonToCsv(list);
      const filename = `orders-export-${new Date().toISOString().slice(0, 10)}.csv`;
      downloadCsv(csv, filename);
      showToast("success", "Orders exported successfully");
    } catch {
      showToast("error", "Failed to export orders");
    }
  };

  return (
    <SideNavLayout>
      <div className="flex flex-col h-full p-4">
        {/* Page Header */}
        <div className="p-4">
          <ATMPageHeader
            moduleName="ORDER"
            pageTitle="Orders"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            buttonProps={{
              btnName: isExportLoading ? "Exporting..." : "Download Excel",
              onClick: handleDownloadExcel,
            }}
            isFilter
            onFilterClick={() => setOpenFilterDialog(true)}
          />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto flex flex-col">
          <ATMTable
            columns={columns}
            rows={rows}
            rowExtraClasses={() => "min-h-[40px]"}
            isLoading={isTableLoading}
            onRowClick={(row) => navigate(`${row?._id}/order-view`)}
          />
        </div>

        <div className="p-4 py-2">
          <ATMPagination
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            rowCount={totalItems}
            rows={rows}
          />
        </div>
        {openFilterDialog ? (
          <OrderFilterDialogWrapper
            onClose={() => {
              setOpenFilterDialog(false);
            }}
          />
        ) : null}
      </div>
    </SideNavLayout>
  );
};

export default OrderListing;
